import { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, Pause } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultMusic = [
  {
    id: '1',
    title: 'Hanuman Chalisa',
    artist: 'Traditional',
    audio_url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
    image_url: 'https://images.unsplash.com/photo-1723707358389-7b47690dff16?w=400&h=400&fit=crop',
    duration: 180,
  },
  {
    id: '2',
    title: 'Om Namah Shivaya',
    artist: 'Devotional',
    audio_url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3',
    image_url: 'https://images.unsplash.com/photo-1759592370294-30f83cf6a609?w=400&h=400&fit=crop',
    duration: 240,
  },
  {
    id: '3',
    title: 'Gayatri Mantra',
    artist: 'Sacred Chants',
    audio_url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3',
    image_url: 'https://images.unsplash.com/photo-1723707358389-7b47690dff16?w=400&h=400&fit=crop',
    duration: 300,
  },
];

export default function MusicPage() {
  const [musicList, setMusicList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlaying, setCurrentPlaying] = useState(null);

  useEffect(() => {
    fetchMusic();
  }, []);

  const fetchMusic = async () => {
    try {
      const response = await axios.get(`${API}/music`);
      if (response.data.length > 0) {
        setMusicList(response.data);
      } else {
        setMusicList(defaultMusic);
      }
    } catch (error) {
      setMusicList(defaultMusic);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading music...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="text-center mb-8 peaceful-enter">
        <h2 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          Bhakti Music
        </h2>
        <p className="text-slate-600">Devotional songs and mantras for spiritual peace</p>
      </div>

      <div className="space-y-4">
        {musicList.map((music, index) => (
          <div
            key={music.id}
            data-testid={`music-item-${index}`}
            className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 hover:shadow-md transition-shadow peaceful-enter"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <div className="flex gap-4 items-center">
              <img
                src={music.image_url}
                alt={music.title}
                className="w-16 h-16 rounded-xl object-cover border-2 border-orange-100"
              />

              <div className="flex-1">
                <h3 className="font-semibold text-slate-800 text-lg" style={{ fontFamily: 'Merriweather, serif' }}>
                  {music.title}
                </h3>
                <p className="text-sm text-slate-600">{music.artist}</p>
                <p className="text-xs text-slate-500">{formatDuration(music.duration)}</p>
              </div>

              <button
                data-testid={`play-music-btn-${index}`}
                onClick={() => setCurrentPlaying(currentPlaying === music.id ? null : music.id)}
                className="w-12 h-12 bg-[#FF9933] hover:bg-[#E68A00] rounded-full flex items-center justify-center transition-all hover:-translate-y-0.5"
              >
                {currentPlaying === music.id ? (
                  <Pause className="w-5 h-5 text-white" fill="white" />
                ) : (
                  <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center text-sm text-slate-500 peaceful-enter">
        <p>Music plays one at a time. No autoplay or distractions.</p>
      </div>
    </div>
  );
}
